import { useEffect, useState, type ReactNode } from 'react';
import { fetchAttendanceDays, fetchMembers, fetchWorkers } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { monthOf, monthRange } from '../lib/attendance-history';
import { todayInZone } from '../lib/attendance-session';
import { KETERANGAN_KODE, hariKerja, rekapBulanan, tanggalBulan } from '../lib/rekap-bulanan';
import { ErrorCard, LoadingState } from '../states';

/**
 * Rekap absensi SEBULAN — matriks orang × tanggal, dibuat untuk dicetak.
 *
 * ADA KARENA REKAP HARIAN TERLALU MAHAL DIPAKAI. Cetak harian (#890) dipakai sehari setelah
 * mendarat, dan batasnya langsung terasa: satu bulan berarti menekan cetak ~30 kali. Yang salah
 * periodenya, bukan tata letaknya — jadi gaya cetak, blok tanda tangan, dan penjaganya dipakai
 * ulang apa adanya; yang baru hanya bentuk datanya.
 *
 * Dicetak MENDATAR (`@page { size: A4 landscape }`). Tiga puluh satu kolom pada A4 potret
 * menyisakan sekitar 5mm per kolom — angka yang tidak terbaca, dan lembar yang tidak terbaca
 * bukan laporan.
 */
export function MonthlyAttendancePage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const workDays = session.policy?.workDays;
  const [month, setMonth] = useState(() => monthOf(todayInZone(new Date(), timezone)));

  const { state, reload } = useAsync(async () => {
    const { from, to } = monthRange(month);
    /*
     * Tiga panggilan sekaligus supaya angka dan nama menggambarkan bulan yang SAMA. Memuatnya
     * terpisah membuka celah waktu yang, tepat saat seseorang check-in, menghasilkan baris tanpa
     * nama atau nama tanpa baris.
     */
    const [workers, members, days] = await Promise.all([
      fetchWorkers(session.auth),
      fetchMembers(session.auth),
      fetchAttendanceDays(session.auth, from, to),
    ]);
    return { baris: rekapBulanan(workers, members, days, month, workDays) };
  }, [session.auth, month, workDays]);

  /* Stempel waktu dipasang lebih dulu, cetak menyusul setelah render — lihat alasan di
     `AttendanceDashboardPage`: mencetak di dalam handler yang sama akan mencetak DOM lama. */
  const [printedAt, setPrintedAt] = useState<string | null>(null);
  useEffect(() => {
    if (printedAt === null) return;
    window.print();
  }, [printedAt]);

  const tanggal = tanggalBulan(month);
  const totalTanpaVerifikasi =
    state.status === 'ready'
      ? state.data.baris.reduce((n, b) => n + b.tanpaVerifikasiLokasi, 0)
      : 0;
  const totalTanpaSelfie =
    state.status === 'ready' ? state.data.baris.reduce((n, b) => n + b.tanpaSelfie, 0) : 0;

  return (
    /* `lembar-bulanan` mengikat orientasi MENDATAR hanya pada halaman ini — lihat
       `@page bulanan` di styles.css. Tanpa pembungkus ini, rekap HARIAN ikut mendatar. */
    <div className="lembar-bulanan">
      <header className="print-only print-header">
        <h1>Rekap Absensi Bulanan</h1>
        <p className="print-sub">
          {namaBulan(month)} · zona waktu {timezone}
        </p>
        <p className="print-ref">
          Organisasi {session.me.organizationId}
          {printedAt !== null ? ` · dicetak ${printedAt}` : ''}
        </p>
      </header>

      <section className="card no-print">
        <div className="row space-between">
          <h2>Rekap bulanan</h2>
          {state.status === 'ready' && state.data.baris.length > 0 ? (
            <button
              type="button"
              onClick={() =>
                setPrintedAt(new Date().toLocaleString('id-ID', { timeZone: timezone }))
              }
            >
              Cetak rekap
            </button>
          ) : null}
        </div>
        <label htmlFor="month">Bulan</label>
        <input id="month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <p className="muted small">
          Satu lembar untuk seluruh bulan. Dicetak mendatar agar {tanggal.length} kolom tanggal
          tetap terbaca.
        </p>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat rekap bulanan…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' ? (
        state.data.baris.length === 0 ? (
          <section className="card">
            <p className="muted small">
              Belum ada pekerja aktif pada organisasi ini, jadi tidak ada yang dapat direkap.
            </p>
          </section>
        ) : (
          <>
            <section className="card matriks-bungkus">
              <table className="matriks">
                <thead>
                  <tr>
                    <th scope="col" className="matriks-nama">
                      Nama
                    </th>
                    {tanggal.map((tgl, i) => (
                      <th
                        scope="col"
                        key={tgl}
                        className={hariKerja(tgl, workDays) ? undefined : 'matriks-libur'}
                      >
                        {i + 1}
                      </th>
                    ))}
                    <th scope="col">H</th>
                    <th scope="col">T</th>
                    <th scope="col">B</th>
                    <th scope="col">I</th>
                    <th scope="col">Tanpa</th>
                    <th scope="col">Telat</th>
                    {/* Lokasi tak terverifikasi — pindah dari lembar harian yang dihapus. */}
                    <th scope="col">Lok?</th>
                    {/* Selfie tak lengkap (item 9, review 12 Sep 2026) — sinyal terpisah dari lokasi. */}
                    <th scope="col">Selfie?</th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.baris.map((b) => (
                    <tr key={b.userId}>
                      <th scope="row" className="matriks-nama">
                        {b.name}
                      </th>
                      {b.sel.map((kode, i) => (
                        <td
                          key={tanggal[i]}
                          className={
                            hariKerja(tanggal[i] as string, workDays) ? undefined : 'matriks-libur'
                          }
                        >
                          {kode}
                        </td>
                      ))}
                      <td>{b.hadir}</td>
                      <td>{b.terlambat}</td>
                      <td>{b.belumLengkap}</td>
                      <td>{b.izin}</td>
                      <td>{b.tanpaCatatanHariKerja}</td>
                      <td>{b.totalTelatMenit > 0 ? `${b.totalTelatMenit}m` : '—'}</td>
                      <td>{b.tanpaVerifikasiLokasi > 0 ? b.tanpaVerifikasiLokasi : '—'}</td>
                      <td>{b.tanpaSelfie > 0 ? b.tanpaSelfie : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/*
              Keterangan kode WAJIB ikut tercetak. Matriks satu huruf tidak dapat dibaca siapa pun
              yang tidak ikut membangunnya, dan lembar yang ditandatangani harus dapat dibaca
              sendiri tanpa bertanya kepada pembuatnya.
            */}
            <section className="card keterangan">
              <p className="muted small">
                {KETERANGAN_KODE.map((k) => `${k.kode} = ${k.arti}`).join('  |  ')}
              </p>
              <p className="muted small">
                Kolom berlatar abu adalah hari di luar hari kerja organisasi. Absensi yang terjadi
                di hari itu tetap tercatat; yang tidak dihitung hanyalah ketidakhadirannya.
              </p>
              <p className="muted small">
                <strong>Lok?</strong> = jumlah hari yang tercatat tetapi lokasinya tidak
                terverifikasi penuh.
              </p>
              <p className="muted small">
                <strong>Selfie?</strong> = jumlah hari yang tercatat tetapi swafotonya tidak lengkap
                (item 9, review 12 Sep 2026) — hanya dihitung pada organisasi yang mewajibkan
                selfie.
              </p>
              {/*
                DITONJOLKAN, bukan disembunyikan di dalam kolom. Sejak cetak harian dihapus,
                lembar inilah satu-satunya yang ditandatangani — dan angka kehadiran yang rapi
                padahal sebagian tanpa verifikasi lokasi adalah kesan keliru yang paling mahal
                yang bisa dibawa selembar kertas bertanda tangan.
              */}
              {totalTanpaVerifikasi > 0 ? (
                <p className="warning">
                  {totalTanpaVerifikasi} hari tercatat tanpa verifikasi lokasi penuh bulan ini.
                  Periksa sebelum memakai rekap ini sebagai dasar keputusan.
                </p>
              ) : null}
              {totalTanpaSelfie > 0 ? (
                <p className="warning">
                  {totalTanpaSelfie} hari tercatat tanpa swafoto lengkap bulan ini. Periksa sebelum
                  memakai rekap ini sebagai dasar keputusan.
                </p>
              ) : null}
            </section>

            <section className="print-only print-sign">
              <div className="print-sign-box">
                <p>Diperiksa dan disahkan,</p>
                <p className="print-sign-date">Tanggal: ..............................</p>
                <div className="print-sign-line" aria-hidden="true" />
                <p className="print-sign-role">Manajer</p>
              </div>
            </section>
          </>
        )
      ) : null}
    </div>
  );
}

/** `2026-08` → `Agustus 2026`. Tanpa `Date`: nama bulan adalah kalender, bukan zona waktu. */
function namaBulan(month: string): string {
  const NAMA = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ];
  const [tahun, bulan] = month.split('-');
  return `${NAMA[Number(bulan) - 1] ?? month} ${tahun}`;
}
